import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import OutlinedCard from '../../common/MediaCard';
import GridViewIcon from '@mui/icons-material/GridView';
import CustomSpeedDial from '../../common/SpeedDial';
import appService from '../../../services/ApplicationService';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import ViewListIcon from '@mui/icons-material/ViewList';
import Modal from '../../common/Modal';
import NormalTextField from '../../common/NormalTextField';
import DynamicSnackbar from '../../common/Snackbar';
import MultipleSelect from '../../common/MultipleSelect';
import companyService from '../../../services/CompanyService';
import SettingsIcon from '@mui/icons-material/Settings';
import userService from '../../../services/UserService';
import userrolesService from '../../../services/UserRoleService';
import { UsersIcon, LinkIcon, Cog6ToothIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingButton from '@mui/lab/LoadingButton';
import FileUploadApplications from '../../common/FileUploadApplications';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05
        }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1
    }
};

const ManageApplicationsv2 = () => {
    const navigate = useNavigate();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [companies, setCompanies] = useState([]);
    const [apps, setApps] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState([]);
    const [selectedType, setSelectedType] = useState([]);
    const [selectedUser, setSelectedUser] = useState([]);
    const [soxRelevant, setSoxRelevant] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCompanies, setExpandedCompanies] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    
    // Bulk upload state variables
    const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
    const [appsUpload, setAppsUpload] = useState([]);
    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [userList, setUserList] = useState([]);

    // Add display mode state
    const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'

    // Toggle company expansion
    const toggleCompanyExpansion = (companyId) => {
        setExpandedCompanies(prev => ({
            ...prev,
            [companyId]: !prev[companyId]
        }));
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [appData, setAppData] = useState({
        COMPANY_ID: '',
        APP_NAME: '',
        APP_DESCRIPTION: '',
        APPLICATION_OWNER: '',
        APP_TYPE: '',
        SOX_INSCOPE: false,
    });

    const appDataWithOwner = {
        ...appData,
        APPLICATION_OWNER: selectedUser.map(id => id.value),
    };

    const createApp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await appService.createApp(appDataWithOwner);
            setApps([...apps, response]);
            setSnackbarMessage('Application successfully created');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            handleCloseCreateModal();

            // Reload the page after a 1-second delay
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            setSnackbarMessage('There was a problem creating the application');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchApps = async () => {
        try {
            const response = await appService.fetchApps();
            setApps(response);
            
            // Initialize all companies as expanded
            const initialExpandedState = {};
            const uniqueCompanyIds = [...new Set(response.map(app => app.COMPANY_ID))];
            uniqueCompanyIds.forEach(id => {
                initialExpandedState[id] = true;
            });
            setExpandedCompanies(initialExpandedState);
        } catch (error) {
            console.error('Error fetching applications:', error);
            setSnackbarMessage('Error fetching applications');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await companyService.fetchCompanies();
            setCompanies(response);
        } catch (error) {
            console.error('Error fetching companies:', error);
            setSnackbarMessage('Error fetching companies');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        fetchApps();
        fetchCompanies();
    }, []);

    const handleOpenCreateModal = () => {
        setOpenCreateModal(true);
    };

    const handleCloseCreateModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenCreateModal(true);
        } else {
            setOpenCreateModal(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAppData({ ...appData, [name]: value });
    };

    const companyList = companies.map(company => ({
        value: company.id,
        label: company.COMPANY_NAME
    }));

    const systemType = [
        { value: 'Operating System', label: 'Operating System' },
        { value: 'Database', label: 'Database' },
        { value: 'Application', label: 'Application' },
        { value: 'Network', label: 'Network' },
        { value: 'Tool', label: 'Tool' }
    ];

    const soxInScope = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ];

    const handleCompanyChange = async (selectedCompany) => {
        setSelectedCompany(selectedCompany);
        setAppData(prevAppData => ({
            ...prevAppData,
            COMPANY_ID: selectedCompany.value
        }));
    };

    const handleTypeChange = async (selectedType) => {
        setSelectedType(selectedType);
        setAppData(prevAppData => ({
            ...prevAppData,
            APP_TYPE: selectedType.value
        }));
    };

    const handleSOXChange = async (selectedType) => {
        setSoxRelevant(selectedType);
        setAppData(prevAppData => ({
            ...prevAppData,
            SOX_INSCOPE: selectedType.value
        }));
    };

    const handleUserChange = (selectedOptions) => {
        setSelectedUser(selectedOptions);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filter apps based on search query
    const filteredApps = apps.filter(app => {
        const appName = app.APP_NAME ? app.APP_NAME.toLowerCase() : '';
        const companyName = app.COMPANY_NAME ? app.COMPANY_NAME.toLowerCase() : '';
        const appType = app.APP_TYPE ? app.APP_TYPE.toLowerCase() : '';
        const query = searchQuery.toLowerCase();
        
        return appName.includes(query) || companyName.includes(query) || appType.includes(query);
    });

    // Group applications by company
    const getGroupedApps = () => {
        const grouped = {};
        
        filteredApps.forEach(app => {
            const companyId = app.COMPANY_ID || 'unknown';
            const companyName = app.COMPANY_NAME || 'Unknown Company';
            
            if (!grouped[companyId]) {
                grouped[companyId] = {
                    id: companyId,
                    name: companyName,
                    apps: []
                };
            }
            
            grouped[companyId].apps.push(app);
        });
        
        // Convert to array and sort by company name
        return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
    };

    const groupedApps = getGroupedApps();

    // Check if form is valid
    const isFormValid = () => {
        return (
            appData.COMPANY_ID &&
            appData.APP_NAME &&
            appData.APP_TYPE
        );
    };

    // Handler for opening the bulk upload modal
    const handleOpenBulkUploadModal = () => {
        setOpenBulkUploadModal(true);
    };

    // Handler for closing the bulk upload modal
    const handleCloseBulkUploadModal = (event, reason) => {
        // Only prevent default closing if reason is backdropClick
        if (reason === 'backdropClick') {
            return;
        }
        // For any other reason, close the modal
        setOpenBulkUploadModal(false);
        
        // Reset upload states when closing the modal
        setAppsUpload([]);
        setIsUploadComplete(false);
    };

    // Handler for receiving data from FileUploadApplications component
    const handleOnApplicationsData = (data) => {
        console.log('Received data from FileUploadApplications:', data);
        if (Array.isArray(data) && data.length > 0) {
            // Check for duplicate APP_NAME + COMPANY combinations against existing apps in the database
            const duplicateApps = data.filter(newApp => {
                const newAppCompanyId = newApp.COMPANY_ID;
                
                return apps.some(existingApp => {
                    // If the same app name exists
                    if (existingApp.APP_NAME === newApp.APP_NAME) {
                        // Check if company identifiers match (could be ID or name)
                        const companyMatch = 
                            // Direct ID match
                            existingApp.COMPANY_ID === newAppCompanyId || 
                            // Company name match (the field is actually a company name)
                            existingApp.COMPANY_NAME === newAppCompanyId ||
                            // Company name in our existing app matches the new app's company ID
                            (companies.find(c => c.id === existingApp.COMPANY_ID)?.COMPANY_NAME === newAppCompanyId);
                            
                        return companyMatch;
                    }
                    return false;
                });
            });
            
            if (duplicateApps.length > 0) {
                // Format the duplicate entries for display
                const displayInfo = duplicateApps.map(app => `"${app.APP_NAME}" for company "${app.COMPANY_ID}"`);
                setSnackbarMessage(`Duplicate applications found: ${displayInfo.join(', ')}. These application names already exist for the same companies in the system. Application names must be unique within each company, but can be reused across different companies.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            setAppsUpload(data);
            setIsUploadComplete(false);
            
            // Show notification when data is loaded
            setSnackbarMessage(`Loaded ${data.length} applications ready to upload`);
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
        } else {
            setAppsUpload([]);
            console.warn('Received empty or invalid data from file upload');
        }
    };

    // Handler for the upload button click
    const handleBulkUploadClick = async () => {
        if (!appsUpload || appsUpload.length === 0) {
            setSnackbarMessage('No applications to upload');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }
        
        try {
            setIsUploadLoading(true);
            
            // Call your service method to bulk upload applications
            const response = await appService.bulkCreateApps(appsUpload);
            
            if (response) {
                console.log('Upload successful, received data:', response);
                // Update the apps state with the new applications
                setApps(prevApps => [...prevApps, ...response]);
                
                setIsUploadLoading(false);
                setIsUploadComplete(true);
                
                setSnackbarMessage('Successfully uploaded applications');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                // Show success state briefly, then refresh the page
                setTimeout(() => {
                    window.location.reload(); // Refresh the page
                }, 1500);
            } else {
                throw new Error('No data returned from server');
            }
        } catch (error) {
            console.error('Error uploading applications:', error);
            setIsUploadLoading(false);
            setIsUploadComplete(false);
            
            setSnackbarMessage('Error uploading applications: ' + (error.message || 'Unknown error'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Function to download CSV template for bulk upload
    const downloadTemplate = () => {
        // Create template CSV content - headers match exactly with the application model fields
        const csvContent = [
            "COMPANY_ID,APP_NAME,APP_DESCRIPTION,APP_TYPE,SOX_INSCOPE,APPLICATION_OWNER",
            "Global Corp,Finance ERP,Enterprise resource planning system for finance department,Application,true,john.doe@example.com",
            "Tech Systems,HR Portal,Human resources management system,Application,false,jane.smith@example.com",
            "ACME Inc,Network Monitor,Network monitoring tool,Tool,false,admin@example.com"
        ].join("\n");
        
        // Create a blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary link to trigger download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Applications_Template.csv");
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const customMainContent = (
        <div className="min-h-screen bg-gray-50">
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                            Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        System Settings
                    </mui.Link>
                    <mui.Typography color="text.primary">Applications</mui.Typography>
                </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <GridViewIcon className="h-7 w-7 mr-2 text-blue-500" aria-hidden="true" />
                        <mui.Box sx={{ flexGrow: 1 }}>
                            <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                Applications
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Browse applications by company
                </mui.Typography>
                        </mui.Box>
                        <mui.ButtonGroup size="small" sx={{ ml: 2 }}>
                            <mui.Button 
                                variant={displayMode === 'grid' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('grid')}
                                startIcon={<GridViewIcon fontSize="small" />}
                                sx={{ textTransform: 'none' }}
                            >
                                Grid
                            </mui.Button>
                            <mui.Button 
                                variant={displayMode === 'list' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('list')}
                                startIcon={<ViewListIcon fontSize="small" />}
                                sx={{ textTransform: 'none' }}
                            >
                                List
                            </mui.Button>
                        </mui.ButtonGroup>
                    </mui.Box>

                    <mui.Box sx={{ mb: 3 }}>
                        <mui.TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search applications by name, company, or type..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <mui.InputAdornment position="start">
                                        <mui.Icon>search</mui.Icon>
                                    </mui.InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.08)'
                                    }
                                }
                            }}
                            size="small"
                        />
                    </mui.Box>

                <Separator />
             
                    <mui.Box sx={{ mt: 3 }}>
                        {groupedApps.length === 0 ? (
                            <mui.Paper 
                                elevation={0}
                                sx={{ 
                                    p: 4, 
                                    textAlign: 'center',
                                    backgroundColor: '#f8fafc',
                                    border: '1px dashed rgba(0, 0, 0, 0.12)',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    No applications found. Try adjusting your search or add a new application.
                                </mui.Typography>
                            </mui.Paper>
                        ) : (
                            groupedApps.map((company) => (
                                <mui.Box key={company.id} sx={{ mb: 2 }}>
                                    <mui.Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: 'rgba(0, 0, 0, 0.06)',
                                        }}
                                    >
                                        <mui.Button 
                                            fullWidth
                                            onClick={() => toggleCompanyExpansion(company.id)}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                textAlign: 'left',
                                                justifyContent: 'flex-start',
                                                borderRadius: 0,
                                                color: 'text.primary',
                                                backgroundColor: 'white',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                                }
                                            }}
                                        >
                                            {expandedCompanies[company.id] ? (
                                                <KeyboardArrowDownIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            ) : (
                                                <KeyboardArrowRightIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            )}
                                            <BusinessIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
                                            <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {company.name}
                                            </mui.Typography>
                                            <mui.Chip
                                                label={`${company.apps.length} ${company.apps.length === 1 ? 'Application' : 'Applications'}`}
                                                size="small"
                                                sx={{ 
                                                    ml: 2,
                                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                    color: 'primary.main',
                                                    fontWeight: 500,
                                                    fontSize: '0.7rem',
                                                    height: '22px'
                                                }}
                                            />
                                        </mui.Button>
                                        
                                        {expandedCompanies[company.id] && (
                                            <mui.Box 
                                                sx={{ 
                                                    p: 2, 
                                                    backgroundColor: '#f8fafc',
                                                    borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                                                }}
                                            >
                                                {displayMode === 'grid' ? (
                                                    <mui.Grid container spacing={2}>
                                                        {company.apps.map((app) => (
                                                            <mui.Grid item xs={12} sm={6} md={4} key={app.id}>
                                                                <mui.Paper
                                                                    elevation={0}
                                                                    sx={{
                                                                        p: 2,
                                                                        borderRadius: 2,
                                                                        height: '100%',
                                                                        border: '1px solid rgba(0, 0, 0, 0.06)',
                                                                        backgroundColor: 'white',
                                                                        transition: 'all 0.2s',
                                                                        '&:hover': {
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                                            borderColor: 'rgba(25, 118, 210, 0.3)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <mui.Box sx={{ mb: 2 }}>
                                                                        <mui.Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                                            {app.APP_NAME}
                                                                        </mui.Typography>
                                                                        
                                                                        <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                                            {app.APP_TYPE && (
                                                                                <mui.Chip 
                                                                                    label={app.APP_TYPE} 
                                                                                    size="small"
                                                                                    sx={{ 
                                                                                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                                                                        color: '#4caf50',
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.65rem',
                                                                                        height: '20px',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            
                                                                            {app.SOX_INSCOPE && (
                                                                                <mui.Chip 
                                                                                    label="SOX Relevant" 
                                                                                    size="small"
                                                                                    sx={{ 
                                                                                        backgroundColor: 'rgba(245, 124, 0, 0.08)',
                                                                                        color: '#f57c00',
                                                                                        fontWeight: 500,
                                                                                        fontSize: '0.65rem',
                                                                                        height: '20px',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </mui.Box>
                                                                        
                                                                        {app.APP_DESCRIPTION && (
                                                                            <mui.Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    color: 'text.secondary',
                                                                                    mt: 1,
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 3,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                            >
                                                                                {app.APP_DESCRIPTION}
                                                                            </mui.Typography>
                                                                        )}
                                                                    </mui.Box>
                                                                    
                                                                    <mui.Box sx={{ 
                                                                        display: 'flex', 
                                                                        justifyContent: 'space-between', 
                                                                        mt: 'auto',
                                                                        pt: 1,
                                                                        borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                                                                    }}>
                                                                        <mui.Button
                                                                            component="a"
                                                                            href={`/applications/manageusers/${app.id}`}
                                                                            startIcon={<UsersIcon className="size-4" />}
                                                                            size="small"
                                                                            sx={{ 
                                                                                textTransform: 'none',
                                                                                color: 'text.secondary',
                                                                                '&:hover': { color: 'primary.main' }
                                                                            }}
                                                                        >
                                                Users
                                                                        </mui.Button>
                                                                        
                                                                        <mui.Button
                                                                            component="a"
                                                href={`/interfaces/applications/${app.id}`}
                                                                            startIcon={<LinkIcon className="size-4" />}
                                                                            size="small"
                                                                            sx={{ 
                                                                                textTransform: 'none',
                                                                                color: 'text.secondary',
                                                                                '&:hover': { color: 'primary.main' }
                                                                            }}
                                                                        >
                                                Interfaces
                                                                        </mui.Button>
                                                                        
                                                                        <mui.Button
                                                                            component="a"
                                                href={`/applications/${app.id}`}
                                                                            startIcon={<Cog6ToothIcon className="size-4" />}
                                                                            size="small"
                                                                            sx={{ 
                                                                                textTransform: 'none',
                                                                                color: 'text.secondary',
                                                                                '&:hover': { color: 'primary.main' }
                                                                            }}
                                                                        >
                                                Manage
                                                                        </mui.Button>
                                                                    </mui.Box>
                                                                </mui.Paper>
                                                            </mui.Grid>
                                                        ))}
                                                    </mui.Grid>
                                                ) : (
                                                    <mui.List 
                                                        sx={{ 
                                                            bgcolor: 'background.paper',
                                                            borderRadius: 2,
                                                            overflow: 'hidden',
                                                            border: '1px solid rgba(0, 0, 0, 0.06)',
                                                        }}
                                                        component={motion.ul}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        transition={{ duration: 0.3 }}
                                                    >
                                                        {company.apps.map((app, index) => (
                                                            <React.Fragment key={app.id}>
                                                                <mui.ListItem 
                                                                    component={motion.li}
                                                                    whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.01)' }}
                                                                    sx={{ 
                                                                        py: 1.5,
                                                                        px: 2,
                                                                    }}
                                                                >
                                                                    <mui.ListItemAvatar>
                                                                        <mui.Avatar 
                                                                            sx={{ 
                                                                                bgcolor: 'primary.main',
                                                                                width: 40,
                                                                                height: 40
                                                                            }}
                                                                        >
                                                                            <AppRegistrationIcon />
                                                                        </mui.Avatar>
                                                                    </mui.ListItemAvatar>
                                                                    <mui.ListItemText
                                                                        primary={
                                                                            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                                    {app.APP_NAME}
                                                                                </mui.Typography>
                                                                                <mui.Box sx={{ display: 'flex', ml: 1.5, gap: 0.5 }}>
                                                                                    {app.APP_TYPE && (
                                                                                        <mui.Chip 
                                                                                            label={app.APP_TYPE} 
                                                                                            size="small"
                                                                                            sx={{ 
                                                                                                backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                                                                                color: '#4caf50',
                                                                                                fontWeight: 500,
                                                                                                fontSize: '0.65rem',
                                                                                                height: '20px',
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    
                                                                                    {app.SOX_INSCOPE && (
                                                                                        <mui.Chip 
                                                                                            label="SOX Relevant" 
                                                                                            size="small"
                                                                                            sx={{ 
                                                                                                backgroundColor: 'rgba(245, 124, 0, 0.08)',
                                                                                                color: '#f57c00',
                                                                                                fontWeight: 500,
                                                                                                fontSize: '0.65rem',
                                                                                                height: '20px',
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </mui.Box>
                                                                            </mui.Box>
                                                                        }
                                                                        secondary={
                                                                            <mui.Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                                                {app.APP_DESCRIPTION || "No description provided"}
                                                                            </mui.Typography>
                                                                        }
                                                                        sx={{ my: 0 }}
                                                                    />
                                                                    <mui.Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <mui.Tooltip title="Manage Users">
                                                                            <mui.IconButton
                                                                                component="a"
                                                                                href={`/applications/manageusers/${app.id}`}
                                                                                size="small"
                                                                                color="default"
                                                                                sx={{ color: 'text.secondary' }}
                                                                            >
                                                                                <UsersIcon className="size-5" />
                                                                            </mui.IconButton>
                                                                        </mui.Tooltip>
                                                                        <mui.Tooltip title="Manage Interfaces">
                                                                            <mui.IconButton
                                                                                component="a"
                                                                                href={`/interfaces/applications/${app.id}`}
                                                                                size="small"
                                                                                color="default"
                                                                                sx={{ color: 'text.secondary' }}
                                                                            >
                                                                                <LinkIcon className="size-5" />
                                                                            </mui.IconButton>
                                                                        </mui.Tooltip>
                                                                        <mui.Button
                                                                            component="a"
                                                                            href={`/applications/${app.id}`}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            startIcon={<Cog6ToothIcon className="size-4" />}
                                                                            sx={{ 
                                                                                ml: 1,
                                                                                textTransform: 'none',
                                                                                fontWeight: 500,
                                                                                borderRadius: 1.5,
                                                                                borderColor: 'rgba(0,0,0,0.12)'
                                                                            }}
                                                                        >
                                                                            Manage
                                                                        </mui.Button>
                                                                    </mui.Box>
                                                                </mui.ListItem>
                                                                {index < company.apps.length - 1 && <mui.Divider component="li" />}
                                                            </React.Fragment>
                                                        ))}
                                                    </mui.List>
                                                )}
                                            </mui.Box>
                                        )}
                                    </mui.Paper>
                                </mui.Box>
                            ))
                        )}
                    </mui.Box>

                <Modal
                    open={openCreateModal}
                    onClose={handleCloseCreateModal}
                    header={
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AppRegistrationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                            <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Setup New Application
                            </mui.Typography>
                        </mui.Box>
                    }
                    body={
                        <mui.Box sx={{ mt: 2 }}>
                            <mui.Alert 
                                severity="info" 
                                sx={{ 
                                    mb: 3, 
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                    color: 'primary.main',
                                    '& .MuiAlert-icon': {
                                        color: 'primary.main'
                                    }
                                }}
                            >
                                Required fields are marked with an asterisk (*).
                            </mui.Alert>
                            
                            <mui.Stack spacing={3}>
                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="company" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Company*
                                    </mui.InputLabel>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    placeholderText="Select Company"
                                    selectOptions={companyList}
                                    value={selectedCompany}
                                    handleChange={handleCompanyChange}
                                    />
                                </mui.Box>

                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="APP_NAME" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Application Name*
                                    </mui.InputLabel>
                                    <mui.TextField
                                        id="APP_NAME"
                                name="APP_NAME"
                                value={appData.APP_NAME}
                                onChange={handleChange}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder="Enter application name"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease-in-out',
                                                '& fieldset': {
                                                    borderColor: 'rgba(0,0,0,0.06)',
                                                    transition: 'all 0.2s',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <mui.InputAdornment position="start">
                                                    <AppRegistrationIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                                                </mui.InputAdornment>
                                            ),
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="type" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Application Type*
                                    </mui.InputLabel>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    placeholderText="Select Type"
                                    selectOptions={systemType}
                                    value={selectedType}
                                    handleChange={handleTypeChange}
                                />
                                </mui.Box>

                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="APP_DESCRIPTION" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        Description
                                    </mui.InputLabel>
                                    <mui.TextField
                                        id="APP_DESCRIPTION"
                                        name="APP_DESCRIPTION"
                                        value={appData.APP_DESCRIPTION}
                                        onChange={handleChange}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={4}
                                        placeholder="Enter application description"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px',
                                                transition: 'all 0.2s ease-in-out',
                                                '& fieldset': {
                                                    borderColor: 'rgba(0,0,0,0.06)',
                                                    transition: 'all 0.2s',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                }
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box>
                                    <mui.InputLabel 
                                        htmlFor="sox" 
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            mb: 0.75, 
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}
                                    >
                                        SOX Relevant?
                                    </mui.InputLabel>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    placeholderText="SOX Relevant?"
                                    selectOptions={soxInScope}
                                    value={soxRelevant}
                                    handleChange={handleSOXChange}
                                />
                                </mui.Box>
                            </mui.Stack>
                        </mui.Box>
                    }
                    footer={
                        <mui.Stack direction="row" spacing={1} justifyContent="flex-end">
                            <mui.Button 
                                onClick={handleCloseCreateModal} 
                                variant="outlined"
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                }}
                            >
                                Cancel
                            </mui.Button>
                            <mui.Button 
                                onClick={createApp} 
                                variant="contained"
                                disabled={!isFormValid() || isLoading}
                                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    boxShadow: 'none',
                                    '&:disabled': {
                                        backgroundColor: isLoading ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                                        color: isLoading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                                        opacity: isLoading ? 0.7 : 1
                                    }
                                }}
                            >
                                {isLoading ? 'Creating...' : 'Create Application'}
                            </mui.Button>
                        </mui.Stack>
                    }
                />

                <Modal
                    open={openBulkUploadModal}
                    size={'lg'}
                    onClose={handleCloseBulkUploadModal}
                    header={
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', padding: '16px 24px' }}>
                            <CloudUploadIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: '1.75rem' }} />
                            <mui.Box>
                                <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Bulk Upload Applications
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                    Upload multiple applications at once using a CSV file
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    }
                    body={
                        <FileUploadApplications onApplicationsDataChange={handleOnApplicationsData} />
                    }
                    footer={
                        <mui.Box sx={{ 
                            padding: '16px 24px', 
                            display: 'flex', 
                            alignItems: 'center',
                            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                        }}>
                            <mui.Button
                                variant="text"
                                startIcon={<DownloadIcon />}
                                onClick={downloadTemplate}
                                sx={{
                                    mr: 'auto',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: 'primary.main',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                Download Template
                            </mui.Button>
                            
                            <mui.Button 
                                onClick={handleCloseBulkUploadModal}
                                variant="outlined"
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: 'rgba(0, 0, 0, 0.12)',
                                    minWidth: '100px',
                                    ml: 2
                                }}
                            >
                                {isUploadComplete ? 'Close' : 'Cancel'}
                            </mui.Button>
                            
                            <LoadingButton
                                onClick={handleBulkUploadClick}
                                loading={isUploadLoading}
                                loadingPosition="start"
                                startIcon={isUploadComplete ? <CloudDoneIcon /> : <CloudUploadIcon />}
                                variant="contained"
                                disabled={(!appsUpload || appsUpload.length === 0) || isUploadComplete}
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    boxShadow: 'none',
                                    minWidth: '180px',
                                    ml: 2,
                                    '&.Mui-disabled': { 
                                        color: isUploadComplete ? 'white' : 'rgba(255, 255, 255, 0.7)', 
                                        backgroundColor: isUploadComplete ? 'success.main' : 'primary.main',
                                        opacity: isUploadComplete ? 1 : 0.7
                                    }
                                }}
                            >
                                {isUploadLoading ? 'Uploading...' : isUploadComplete ? 'Upload Complete' : 'Upload Applications'}
                            </LoadingButton>
                        </mui.Box>
                    }
                />

                <CustomSpeedDial 
                    actions={[
                        { 
                            name: 'Create Application', 
                            icon: <AddIcon />, 
                            onClick: handleOpenCreateModal 
                        },
                        { 
                            name: 'Bulk Upload Applications', 
                            icon: <CloudUploadIcon />, 
                            onClick: handleOpenBulkUploadModal 
                        }
                    ]} 
                />

                <DynamicSnackbar
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={handleSnackbarClose}
                />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default ManageApplicationsv2;